import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ServicesPage() {
  const [selectedPackage, setSelectedPackage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formDataEncoded = new URLSearchParams();
      formDataEncoded.append('name', formData.name);
      formDataEncoded.append('email', formData.email);
      formDataEncoded.append('phone', formData.phone);
      formDataEncoded.append('serviceType', formData.serviceType || selectedPackage);
      formDataEncoded.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d621a4tju5c4mq10agfg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataEncoded.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you! We will contact you within 24-48 hours to schedule your consultation.');
        setFormData({ name: '', email: '', phone: '', serviceType: '', message: '' });
        setSelectedPackage('');
      } else {
        setSubmitMessage('Something went wrong. Please try again or contact us directly.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const lifeCoachingPackages = [
    {
      name: 'Discovery Session',
      price: 75,
      duration: '60 minutes',
      description: 'Perfect for first-time clients to explore coaching and discuss your goals',
      features: [
        'One-on-one consultation',
        'Goal assessment',
        'Personalized action plan outline',
        'Resource recommendations',
      ],
      popular: false,
    },
    {
      name: 'Transformation Package',
      price: 350,
      duration: '4 Sessions',
      description: 'Ideal for focused work on specific life challenges or transitions',
      features: [
        'Four 60-minute sessions',
        'Weekly check-ins via email',
        'Customized workbook',
        'Progress tracking tools',
        'Email support between sessions',
      ],
      popular: true,
    },
    {
      name: 'Empowerment Journey',
      price: 750,
      duration: '8 Sessions',
      description: 'Comprehensive coaching for deep personal transformation',
      features: [
        'Eight 60-minute sessions',
        'Bi-weekly sessions over 4 months',
        'Unlimited email support',
        'Personalized wellness plan',
        'Resource library access',
        'One emergency call included',
      ],
      popular: false,
    },
  ];

  const relationshipPackages = [
    {
      name: 'Couples Check-In',
      price: 125,
      duration: '90 minutes',
      description: 'A focused session for couples seeking to strengthen their connection',
      features: [
        'Joint consultation',
        'Communication assessment',
        'Conflict resolution strategies',
        'Take-home exercises',
      ],
      popular: false,
    },
    {
      name: 'Relationship Revival',
      price: 450,
      duration: '4 Sessions',
      description: 'Rebuild and strengthen your relationship foundation',
      features: [
        'Four 90-minute sessions',
        'Individual + couples sessions',
        'Communication toolkit',
        'Weekly exercises',
        'Email support between sessions',
      ],
      popular: true,
    },
    {
      name: 'Deep Connection Program',
      price: 900,
      duration: '8 Sessions',
      description: 'Comprehensive program for lasting relationship transformation',
      features: [
        'Eight 90-minute sessions',
        'Mix of individual & couples work',
        'Unlimited email support',
        'Personalized relationship roadmap',
        'Monthly date night guides',
        'Two emergency calls included',
      ],
      popular: false,
    },
  ];

  const additionalServices = [
    {
      icon: 'ri-group-line',
      name: 'Group Workshops',
      price: 'Starting at $45/person',
      description: 'Interactive workshops on personal growth, relationships, and wellness for groups of 6-20 participants.',
    },
    {
      icon: 'ri-building-line',
      name: 'Corporate Wellness',
      price: 'Custom Pricing',
      description: 'Tailored wellness programs for organizations including stress management, team building, and leadership coaching.',
    },
    {
      icon: 'ri-mic-line',
      name: 'Speaking Engagements',
      price: 'Contact for Rates',
      description: 'Inspiring keynotes and presentations on overcoming adversity, health advocacy, and personal transformation.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/8d3748dcc9d1157b55942b68e57c8b57.jpg"
            alt="Coaching Services"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/90 via-charcoal/70 to-charcoal/50"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block bg-coral/20 text-coral px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Life Coaching & Relationship Services
            </span>
            <h1 className="font-serif text-5xl lg:text-6xl font-bold text-white mb-6">
              Transform Your Life & Relationships
            </h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8">
              Personalized coaching services designed to help you overcome challenges, achieve your goals, and build meaningful connections. Led by LaTonya Duncan—author, survivor, and certified life coach.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#packages"
                className="inline-flex items-center bg-coral text-white px-8 py-4 rounded-full font-semibold hover:bg-coral/90 transition-all cursor-pointer whitespace-nowrap"
              >
                View Packages
                <i className="ri-arrow-down-line ml-2"></i>
              </a>
              <a
                href="#booking"
                className="inline-flex items-center bg-white/10 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-full font-semibold hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
              >
                Book Consultation
                <i className="ri-calendar-line ml-2"></i>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-charcoal mb-4">
              Why Choose LaTonya?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real experience. Real transformation. Real results.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: 'ri-heart-pulse-line',
                title: 'Survivor\'s Wisdom',
                description: 'Two-time stroke and cervical cancer survivor bringing authentic understanding to your journey',
              },
              {
                icon: 'ri-award-line',
                title: 'Certified Expert',
                description: 'Certified Life Coach and Relationship Specialist with years of professional experience',
              },
              {
                icon: 'ri-book-open-line',
                title: 'Published Author',
                description: 'Author sharing insights and strategies for personal growth and transformation',
              },
              {
                icon: 'ri-user-heart-line',
                title: 'Holistic Approach',
                description: 'Focus on mind, body, and spirit wellness for complete transformation',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all text-center"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-coral/10 rounded-xl mx-auto mb-4">
                  <i className={`${item.icon} text-3xl text-coral`}></i>
                </div>
                <h3 className="font-bold text-lg text-charcoal mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Life Coaching Packages */}
      <section id="packages" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-coral to-pink rounded-2xl mx-auto mb-6">
              <i className="ri-compass-3-line text-4xl text-white"></i>
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-charcoal mb-4">
              Life Coaching Packages
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Navigate life's challenges with clarity, confidence, and personalized support
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {lifeCoachingPackages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all ${
                  pkg.popular ? 'ring-2 ring-coral' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-coral text-white px-4 py-1 rounded-full text-sm font-semibold whitespace-nowrap">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="font-serif text-2xl font-bold text-charcoal mb-2">{pkg.name}</h3>
                  <p className="text-gray-500 text-sm mb-4">{pkg.duration}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-coral">${pkg.price}</span>
                  </div>
                </div>
                <p className="text-gray-600 text-center mb-6 text-sm leading-relaxed">
                  {pkg.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i className="ri-check-line text-coral text-lg mt-0.5"></i>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#booking"
                  onClick={() => setSelectedPackage(`Life Coaching - ${pkg.name}`)}
                  className={`block w-full text-center py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    pkg.popular
                      ? 'bg-coral text-white hover:bg-coral/90'
                      : 'bg-coral/10 text-coral hover:bg-coral hover:text-white'
                  }`}
                >
                  Select Package
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Relationship Packages */}
      <section className="py-20 bg-gradient-to-br from-coral/5 via-pink/5 to-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-pink to-coral rounded-2xl mx-auto mb-6">
              <i className="ri-hearts-line text-4xl text-white"></i>
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-charcoal mb-4">
              Relationship Specialist Packages
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Strengthen connections, improve communication, and build lasting relationships
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {relationshipPackages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all ${
                  pkg.popular ? 'ring-2 ring-pink' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-pink to-coral text-white px-4 py-1 rounded-full text-sm font-semibold whitespace-nowrap">
                      Best Value
                    </span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="font-serif text-2xl font-bold text-charcoal mb-2">{pkg.name}</h3>
                  <p className="text-gray-500 text-sm mb-4">{pkg.duration}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-pink">${pkg.price}</span>
                  </div>
                </div>
                <p className="text-gray-600 text-center mb-6 text-sm leading-relaxed">
                  {pkg.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i className="ri-check-line text-pink text-lg mt-0.5"></i>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#booking"
                  onClick={() => setSelectedPackage(`Relationship - ${pkg.name}`)}
                  className={`block w-full text-center py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    pkg.popular
                      ? 'bg-gradient-to-r from-pink to-coral text-white hover:opacity-90'
                      : 'bg-pink/10 text-pink hover:bg-pink hover:text-white'
                  }`}
                >
                  Select Package
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-charcoal mb-4">
              Additional Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Customized solutions for groups, organizations, and special events
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-cream rounded-3xl p-8 hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-white rounded-2xl shadow-md mb-6">
                  <i className={`${service.icon} text-3xl text-coral`}></i>
                </div>
                <h3 className="font-serif text-2xl font-bold text-charcoal mb-2">{service.name}</h3>
                <p className="text-coral font-semibold mb-4">{service.price}</p>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 bg-charcoal text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl lg:text-5xl font-bold mb-4">
              What to Expect
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Your journey to transformation in four simple steps
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Book Consultation',
                description: 'Schedule your free 15-minute discovery call to discuss your needs and goals',
              },
              {
                step: '02',
                title: 'Choose Your Path',
                description: 'Select the package that best fits your situation and desired outcomes',
              },
              {
                step: '03',
                title: 'Begin Sessions',
                description: 'Start your personalized coaching journey with dedicated support',
              },
              {
                step: '04',
                title: 'Transform & Thrive',
                description: 'Implement strategies, track progress, and celebrate your growth',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-6xl font-bold text-coral/30 mb-4">{item.step}</div>
                <h3 className="font-bold text-xl mb-3">{item.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-charcoal mb-4">
              Client Testimonials
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real stories from real people who transformed their lives
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Michelle R.',
                role: 'Life Coaching Client',
                quote: 'LaTonya helped me find my voice again after years of feeling lost. Her personal experience as a survivor gave her insights that no textbook could teach. I am forever grateful.',
                image: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20African%20American%20woman%20in%20her%2040s%20warm%20smile%20confident%20expression%20soft%20natural%20lighting%20neutral%20background%20business%20casual%20attire&width=200&height=200&seq=testimonial-1&orientation=squarish',
              },
              {
                name: 'David & Karen T.',
                role: 'Couples Coaching Clients',
                quote: 'After 15 years of marriage, we were struggling. LaTonya gave us the tools to communicate again and rediscover why we fell in love. Our relationship is stronger than ever.',
                image: 'https://readdy.ai/api/search-image?query=Happy%20middle%20aged%20couple%20African%20American%20smiling%20together%20warm%20lighting%20casual%20portrait%20style%20genuine%20connection%20neutral%20background&width=200&height=200&seq=testimonial-2&orientation=squarish',
              },
              {
                name: 'Jasmine W.',
                role: 'Transformation Package Client',
                quote: 'The 4-session package changed my life. LaTonya helped me set clear goals and actually achieve them. Her support between sessions made all the difference.',
                image: 'https://readdy.ai/api/search-image?query=Young%20professional%20Black%20woman%20in%20her%2030s%20confident%20smile%20natural%20makeup%20professional%20headshot%20soft%20lighting%20neutral%20background&width=200&height=200&seq=testimonial-3&orientation=squarish',
              },
              {
                name: 'Robert J.',
                role: 'Empowerment Journey Client',
                quote: 'After my divorce, I was completely lost. LaTonya helped me rebuild my confidence and create a vision for my future. The 8-session program was worth every penny.',
                image: 'https://readdy.ai/api/search-image?query=Professional%20African%20American%20man%20in%20his%2050s%20distinguished%20look%20warm%20genuine%20smile%20wearing%20casual%20blazer%20soft%20natural%20lighting%20neutral%20cream%20background&width=200&height=200&seq=testimonial-4&orientation=squarish',
              },
              {
                name: 'Angela & Marcus P.',
                role: 'Deep Connection Program Clients',
                quote: 'We were on the verge of separation. LaTonya helped us understand each other in ways we never had before. Our marriage is now thriving and we are better partners and parents.',
                image: 'https://readdy.ai/api/search-image?query=Happy%20African%20American%20couple%20in%20their%2040s%20embracing%20and%20smiling%20warmly%20together%20casual%20elegant%20attire%20soft%20natural%20lighting%20neutral%20background%20genuine%20love%20connection&width=200&height=200&seq=testimonial-5&orientation=squarish',
              },
              {
                name: 'Tanya S.',
                role: 'Discovery Session Client',
                quote: 'Even just one session with LaTonya opened my eyes. She helped me see patterns I had been blind to for years. I immediately signed up for the full package.',
                image: 'https://readdy.ai/api/search-image?query=Young%20professional%20woman%20in%20her%20late%2020s%20bright%20smile%20hopeful%20expression%20wearing%20professional%20attire%20soft%20natural%20lighting%20neutral%20background%20confident%20and%20optimistic&width=200&height=200&seq=testimonial-6&orientation=squarish',
              },
              {
                name: 'Patricia M.',
                role: 'Corporate Wellness Client',
                quote: 'LaTonya led a workshop for our team and the impact was immediate. Employee morale improved and our communication as a team transformed. We have booked her for quarterly sessions.',
                image: 'https://readdy.ai/api/search-image?query=Professional%20African%20American%20woman%20executive%20in%20her%2040s%20confident%20smile%20wearing%20elegant%20business%20attire%20soft%20natural%20lighting%20neutral%20office%20background%20leadership%20presence&width=200&height=200&seq=testimonial-7&orientation=squarish',
              },
              {
                name: 'James & Linda H.',
                role: 'Relationship Revival Clients',
                quote: 'We thought we knew everything about each other after 20 years. LaTonya showed us there was so much more to discover. Our connection is deeper than our honeymoon days.',
                image: 'https://readdy.ai/api/search-image?query=Mature%20couple%20in%20their%2050s%20African%20American%20holding%20hands%20smiling%20warmly%20at%20each%20other%20casual%20elegant%20attire%20soft%20warm%20lighting%20neutral%20background%20genuine%20affection&width=200&height=200&seq=testimonial-8&orientation=squarish',
              },
              {
                name: 'Denise K.',
                role: 'Life Coaching Client',
                quote: 'As a single mom, I felt overwhelmed and stuck. LaTonya helped me prioritize myself without guilt and create balance. My kids have noticed the positive change in me.',
                image: 'https://readdy.ai/api/search-image?query=Warm%20smiling%20single%20mother%20in%20her%2030s%20African%20American%20woman%20confident%20and%20peaceful%20expression%20casual%20comfortable%20attire%20soft%20natural%20lighting%20neutral%20background%20hopeful%20and%20strong&width=200&height=200&seq=testimonial-9&orientation=squarish',
              },
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-3xl p-8 shadow-lg"
              >
                <div className="flex items-center gap-4 mb-6">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-bold text-charcoal">{testimonial.name}</h4>
                    <p className="text-coral text-sm">{testimonial.role}</p>
                  </div>
                </div>
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400"></i>
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed italic">"{testimonial.quote}"</p>
              </motion.div>
            ))}
          </div>

          {/* Overall Rating */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-12 bg-white rounded-2xl p-8 text-center shadow-lg max-w-2xl mx-auto"
          >
            <div className="flex justify-center items-center gap-3 mb-3">
              <span className="text-5xl font-bold text-charcoal">4.9</span>
              <div className="flex text-yellow-400">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-2xl"></i>
                ))}
              </div>
            </div>
            <p className="text-gray-600">
              Based on <span className="font-semibold text-charcoal">200+ client reviews</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section id="booking" className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-charcoal mb-4">
              Book Your Consultation
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Take the first step toward transformation. Fill out the form below and we'll contact you within 24-48 hours.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-cream rounded-3xl p-8 md:p-12 shadow-xl"
          >
            <form onSubmit={handleSubmit} data-readdy-form id="services-booking-form" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-charcoal mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-coral/50 focus:border-coral transition-all text-sm"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-charcoal mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-coral/50 focus:border-coral transition-all text-sm"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-charcoal mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-coral/50 focus:border-coral transition-all text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label htmlFor="serviceType" className="block text-sm font-semibold text-charcoal mb-2">
                    Service Interest *
                  </label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType || selectedPackage}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-coral/50 focus:border-coral transition-all text-sm cursor-pointer"
                  >
                    <option value="">Select a service...</option>
                    <optgroup label="Life Coaching">
                      <option value="Life Coaching - Discovery Session">Discovery Session ($75)</option>
                      <option value="Life Coaching - Transformation Package">Transformation Package ($350)</option>
                      <option value="Life Coaching - Empowerment Journey">Empowerment Journey ($750)</option>
                    </optgroup>
                    <optgroup label="Relationship Services">
                      <option value="Relationship - Couples Check-In">Couples Check-In ($125)</option>
                      <option value="Relationship - Relationship Revival">Relationship Revival ($450)</option>
                      <option value="Relationship - Deep Connection Program">Deep Connection Program ($900)</option>
                    </optgroup>
                    <optgroup label="Additional Services">
                      <option value="Group Workshop">Group Workshop</option>
                      <option value="Corporate Wellness">Corporate Wellness</option>
                      <option value="Speaking Engagement">Speaking Engagement</option>
                    </optgroup>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-charcoal mb-2">
                  Tell Us About Your Goals
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-coral/50 focus:border-coral transition-all text-sm resize-none"
                  placeholder="Share what you hope to achieve through coaching..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <div className="text-center pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center bg-gradient-to-r from-coral to-pink text-white px-10 py-4 rounded-full font-semibold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Request Consultation'}
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>

              {submitMessage && (
                <p className={`text-center text-sm ${submitMessage.includes('Thank') ? 'text-green-600' : 'text-red-500'}`}>
                  {submitMessage}
                </p>
              )}
            </form>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-charcoal mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                question: 'How do coaching sessions work?',
                answer: 'Sessions are conducted via video call (Zoom) or phone, making it convenient for you regardless of location. Each session is tailored to your specific needs and goals, with actionable takeaways you can implement immediately.',
              },
              {
                question: 'What is the difference between life coaching and therapy?',
                answer: 'Life coaching focuses on your present and future goals, helping you create actionable plans for growth and achievement. While therapy often addresses past trauma and mental health diagnoses, coaching is about moving forward and maximizing your potential.',
              },
              {
                question: 'Can I switch packages after starting?',
                answer: 'Absolutely! If you start with a smaller package and decide you want more support, we can upgrade your package at any time. The difference in price will be applied to your new package.',
              },
              {
                question: 'Do you offer payment plans?',
                answer: 'Yes, we offer flexible payment plans for our multi-session packages. Contact us to discuss options that work for your budget.',
              },
              {
                question: 'What if I need to reschedule a session?',
                answer: 'We understand life happens! Sessions can be rescheduled with at least 24 hours notice at no additional charge. Same-day cancellations may be subject to a fee.',
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-md"
              >
                <h3 className="font-bold text-lg text-charcoal mb-2">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-coral to-pink">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Life?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Don't wait another day to start your journey toward the life and relationships you deserve.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#booking"
                className="inline-flex items-center bg-white text-coral px-8 py-4 rounded-full font-semibold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap"
              >
                Book Now
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center bg-white/10 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-full font-semibold hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
              >
                Have Questions?
                <i className="ri-question-line ml-2"></i>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
